import {
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";

import AvailabilityPanel from "../../components/director/AvailabilityPanel";
import CustomerReceiptsTable from "../../components/director/CustomerReceiptsTable";
import QualityBanner from "../../components/director/QualityBanner";
import ReceivablesFilters from "../../components/director/ReceivablesFilters";
import ReceivablesKpiGrid from "../../components/director/ReceivablesKpiGrid";
import { useDirectorActions, useDirectorContext } from "@contexts/director";
import {
  formatBasis,
  formatBps,
  formatFreshness,
} from "../../utils/directorFormat";

const monthAt = (value) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(value);
  const read = (type) => parts.find((part) => part.type === type)?.value;
  return `${read("year")}-${read("month")}`;
};

const shiftMonth = (competence, delta) => {
  const [year, month] = competence.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
};

export const defaultReceivableRange = (asOf, months = 36) => {
  const parsed = new Date(asOf);
  const now = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const from = monthAt(now);
  return { from, to: shiftMonth(from, months - 1) };
};

const monthsIn = (period) => {
  if (!/^\d{4}-\d{2}$/.test(period?.from || "") || !/^\d{4}-\d{2}$/.test(period?.to || "")) {
    return 36;
  }
  const [fromYear, fromMonth] = period.from.split("-").map(Number);
  const [toYear, toMonth] = period.to.split("-").map(Number);
  const count = (toYear * 12 + toMonth) - (fromYear * 12 + fromMonth) + 1;
  return [12, 24, 36].includes(count) ? count : 36;
};

const CustomerReceipts = () => {
  const state = useDirectorContext();
  const actions = useDirectorActions();
  const navigate = useNavigate();
  const {
    selectedCompanyId,
    accessByCompany,
    metadata,
    period,
    filters,
    receivableFilters,
    customerReceipts,
    customerUnits,
    loading,
    error,
  } = state;
  const access = accessByCompany[selectedCompanyId] || {};
  const defaultPeriod = useMemo(
    () => defaultReceivableRange(access.resolvedAt, 36),
    [access.resolvedAt],
  );
  const hasPeriod = Boolean(period?.from && period?.to);
  const activePeriod = hasPeriod ? period : defaultPeriod;
  const basis = customerReceipts?.basis || receivableFilters?.basis || "nominal";

  useEffect(() => {
    if (!selectedCompanyId) return;
    if (!hasPeriod) {
      actions?.setPeriod(defaultPeriod);
      return;
    }
    actions?.loadCustomerReceipts();
  }, [
    actions,
    selectedCompanyId,
    hasPeriod,
    defaultPeriod.from,
    defaultPeriod.to,
    activePeriod.from,
    activePeriod.to,
    filters.legalEntityId,
    filters.projectKey,
    receivableFilters.customerRef,
    receivableFilters.unitRef,
    receivableFilters.basis,
    receivableFilters.page,
    receivableFilters.pageSize,
  ]);

  useEffect(() => {
    const customerRef = receivableFilters.customerRef;
    if (!selectedCompanyId || !/^[a-f0-9]{64}$/.test(customerRef || "")) return;
    actions?.loadCustomerUnits(customerRef, { unitRef: null, page: 1, pageSize: 100 });
  }, [actions, selectedCompanyId, receivableFilters.customerRef]);

  const retry = () => {
    actions?.clearError();
    actions?.loadCustomerReceipts();
  };
  const customerOptions = (customerReceipts?.items || []).map((item) => ({
    value: item.customerRef,
    label: item.displayName || "Cliente sem nome disponível",
  }));
  const unitOptions = (customerUnits?.items || []).filter((item) => item.unitRef).map((item) => ({
    value: item.unitRef,
    label: item.displayName || "Unidade sem nome disponível",
  }));
  const dataState = customerReceipts?.state;
  const blocking = ["empty", "restricted", "unavailable", "error"].includes(dataState);
  const showContent = Boolean(customerReceipts?.summary && !blocking);
  const keyHandover = customerReceipts?.summary?.keyHandover;
  const pagination = customerReceipts?.pagination;

  return <main className="director-page director-receivables-page">
    <header className="director-page__header">
      <div>
        <span className="director-eyebrow">Diretoria · {access.companyName || "grupo econômico"}</span>
        <h1>Clientes e recebimentos</h1>
        <p>Contratado, recebido e previsto sempre na mesma base monetária.</p>
      </div>
      <span className="director-receivables-basis-chip">
        Base {formatBasis(basis).toLowerCase()}
      </span>
    </header>

    <QualityBanner sources={[customerReceipts, customerUnits]} />

    <ReceivablesFilters
      basisOptions={customerReceipts?.basisOptions || {}}
      customerOptions={customerOptions}
      filters={filters}
      metadata={metadata || {}}
      onFiltersChange={actions?.setFilters}
      onPeriodMonthsChange={(months) => {
        const from = activePeriod.from || defaultPeriod.from;
        actions?.setPeriod({ from, to: shiftMonth(from, months - 1) });
      }}
      onReceivableFiltersChange={actions?.setReceivableFilters}
      periodMonths={monthsIn(activePeriod)}
      receivableFilters={receivableFilters}
      unitOptions={unitOptions}
    />

    {loading.customerReceipts && !customerReceipts && <AvailabilityPanel state="loading" />}
    {error && <AvailabilityPanel
      message={error.message || null}
      onRetry={error.retryable ? retry : null}
      state="error"
    />}
    {customerReceipts && dataState !== "normal" && <AvailabilityPanel
      reasonCode={customerReceipts.reasonCode}
      state={dataState}
    />}

    {showContent && <>
      <ReceivablesKpiGrid summary={customerReceipts.summary} />
      <div className="director-receivables-layout">
        <section className="director-section" aria-labelledby="receivables-table-title">
          <header>
            <div>
              <span className="director-eyebrow">Clientes e unidades</span>
              <h2 id="receivables-table-title">Recebimentos por cliente</h2>
            </div>
            <p>Valores na base {formatBasis(basis).toLowerCase()}; parcelas abertas não são recebidas.</p>
          </header>
          <CustomerReceiptsTable
            companyId={selectedCompanyId}
            items={customerReceipts.items || []}
            onOpenCustomer={(customerRef) => navigate(
              `/dashboard/director/customer-receipts/${customerRef}?company_id=${encodeURIComponent(selectedCompanyId)}`,
            )}
          />
          {pagination && <nav aria-label="Paginação de clientes" className="director-pagination">
            <button
              aria-label="Página anterior"
              disabled={pagination.page <= 1}
              onClick={() => actions?.setReceivableFilters({ page: pagination.page - 1 })}
              type="button"
            >
              Anterior
            </button>
            <span>Página {pagination.page} de {pagination.totalPages || 1}</span>
            <button
              aria-label="Próxima página"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => actions?.setReceivableFilters({ page: pagination.page + 1 })}
              type="button"
            >
              Próxima
            </button>
          </nav>}
        </section>

        <aside className="director-section director-receivables-interpretation">
          <span className="director-eyebrow">Como interpretar</span>
          <h2>{formatBps(customerReceipts.summary.receivedPctBps)} recebido · base {formatBasis(basis).toLowerCase()}</h2>
          <p>O percentual usa somente baixas confirmadas sobre o valor contratado na mesma base.</p>
          <AvailabilityPanel
            reasonCode={keyHandover?.reasonCode || "sales_contracts_restricted_or_rule_missing"}
            state={keyHandover?.state || "unavailable"}
          />
        </aside>
      </div>
      <footer className="director-freshness">
        Última revisão confiável: {formatFreshness(customerReceipts.freshness?.extractedAt)}.
      </footer>
    </>}
  </main>;
};

export default CustomerReceipts;
